import { create } from 'zustand';
import {
  checkAllInboxProviderConnections,
  type InboxProviderId,
} from '@/services/inboxSync';

/** Align with inbox per-provider cache TTL; tab revisits skip refetch inside this window. */
export const DEFAULT_INBOX_CONNECTIONS_STALE_MS = 90_000;

export type InboxConnectionsState = {
  connections: Record<InboxProviderId, boolean>;
  loading: boolean;
  lastFetchedAt: number | null;
  error: string | null;
  reset: () => void;
  setProviderConnected: (provider: InboxProviderId, connected: boolean) => void;
  /** Full snapshot respecting inboxSync RPC cache + batch coalescing. */
  refreshCached: () => Promise<void>;
  /** Bypass RPC read-through cache (OAuth verify, disconnect, explicit invalidate). */
  refreshFresh: () => Promise<void>;
  /**
   * Fetches only if snapshot is older than `thresholdMs` or missing.
   * Use for tab focus so Trips ⇄ Wallet ⇄ Profile does not spam status checks.
   */
  refreshIfStale: (thresholdMs?: number) => Promise<void>;
};

type InboxSetState = {
  (
    partial:
      | Partial<InboxConnectionsState>
      | ((state: InboxConnectionsState) => Partial<InboxConnectionsState>),
  ): void;
};

const snapshotInFlightByMode = new Map<'cached' | 'fresh', Promise<void>>();
const FRESH_REFRESH_MIN_INTERVAL_MS = 30_000;

function runSnapshotRefresh(set: InboxSetState, bypassCache: boolean): Promise<void> {
  const modeKey: 'cached' | 'fresh' = bypassCache ? 'fresh' : 'cached';
  const existing = snapshotInFlightByMode.get(modeKey);
  if (existing) {
    return existing;
  }
  const pending = (async () => {
    set({ loading: true, error: null });
    try {
      const connections = await checkAllInboxProviderConnections(
        bypassCache ? { bypassCache: true } : undefined,
      );
      set({
        connections,
        lastFetchedAt: Date.now(),
        loading: false,
        error: null,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      set({
        loading: false,
        error: message,
      });
    }
  })().finally(() => {
    snapshotInFlightByMode.delete(modeKey);
  });
  snapshotInFlightByMode.set(modeKey, pending);
  return pending;
}

const emptyConnections = (): Record<InboxProviderId, boolean> => ({
  gmail: false,
  outlook: false,
  icloud: false,
});

export const useInboxConnectionsStore = create<InboxConnectionsState>((set, get) => ({
  connections: emptyConnections(),
  loading: false,
  lastFetchedAt: null,
  error: null,

  reset: () => {
    snapshotInFlightByMode.clear();
    set({
      connections: emptyConnections(),
      loading: false,
      lastFetchedAt: null,
      error: null,
    });
  },

  setProviderConnected: (provider, connected) => {
    set((state) => ({
      connections: { ...state.connections, [provider]: connected },
    }));
  },

  refreshCached: () => runSnapshotRefresh(set, false),

  refreshFresh: () => {
    const { lastFetchedAt } = get();
    if (
      lastFetchedAt != null &&
      Date.now() - lastFetchedAt < FRESH_REFRESH_MIN_INTERVAL_MS
    ) {
      return Promise.resolve();
    }
    return runSnapshotRefresh(set, true);
  },

  refreshIfStale: async (thresholdMs = DEFAULT_INBOX_CONNECTIONS_STALE_MS) => {
    const { lastFetchedAt } = get();
    if (
      lastFetchedAt != null &&
      Date.now() - lastFetchedAt < thresholdMs
    ) {
      return;
    }
    await runSnapshotRefresh(set, false);
  },
}));
